import React from "react";
import { useDispatchWrapper } from "../../../hooks";
import { fetchVenueAvailabilityAsync } from "../../../redux/leads/leadsActions";
import { IMultipleEvent } from "../../../redux/leads/leads.types";
import { IBooking } from "../../../redux/booking/booking.types";
import {
  getAvailabilityCheckKey,
  hasAvailabilityInputs,
  isAvailabilityRelevantStatus,
} from "../helpers/leadHelpers";

export interface IEventAvailability {
  checked: boolean;
  availabilty: boolean | null;
  details: IBooking | null;
}

export type IEventAvailabilityMap = { [eventIndex: number]: IEventAvailability };

/** Baseline that makes `getAvailabilityCheckKey` treat the event as changed. */
const blankAvailabilityFields = (event: IMultipleEvent): IMultipleEvent => ({
  ...event,
  preferred_venue_uuid: "",
  function_date: "",
  event_time_uuid: "",
  actual_status: "",
});

/**
 * Runs the venue availability check (`/occupancy/check-availability`) for each
 * event whose venue / date / time / status changed — same rules as the lead form.
 *
 * `checkInitialEvents` also checks the events present on first render, which is
 * what a prefilled form needs (the lead form skips them as they come from the DB).
 */
export const useEventsVenueAvailability = (
  events: IMultipleEvent[],
  options: { checkInitialEvents?: boolean } = {},
) => {
  const { checkInitialEvents = false } = options;
  const dispatch = useDispatchWrapper();

  const [availabilityMap, setAvailabilityMap] =
    React.useState<IEventAvailabilityMap>({});
  const prevEventsRef = React.useRef<IMultipleEvent[] | null>(null);
  const lastCheckKeyRef = React.useRef<Record<number, string>>({});

  React.useEffect(() => {
    if (!events?.length) return;

    if (!prevEventsRef.current) {
      if (!checkInitialEvents) {
        prevEventsRef.current = structuredClone(events);
        return;
      }
      prevEventsRef.current = events.map(blankAvailabilityFields);
    }

    // Event added/removed — indexes shifted, so re-check every event from scratch
    if (prevEventsRef.current.length !== events.length) {
      prevEventsRef.current = events.map(blankAvailabilityFields);
      lastCheckKeyRef.current = {};
      setAvailabilityMap({});
    }

    events.forEach((event, i) => {
      const prev = prevEventsRef.current![i];
      if (!prev) return;

      const key = getAvailabilityCheckKey(event, prev);
      if (!key) return;

      lastCheckKeyRef.current[i] = key;
      dispatch(
        fetchVenueAvailabilityAsync(
          event.preferred_venue_uuid,
          event.event_time_uuid,
          event.function_date as string,
          (_ok, data) => {
            // Ignore a late response for inputs that have since changed
            if (lastCheckKeyRef.current[i] !== key) return;
            setAvailabilityMap((m) => ({
              ...m,
              [i]: {
                checked: true,
                availabilty: data?.availabilty ?? null,
                details: data ?? null,
              },
            }));
          },
        ),
      );
    });

    prevEventsRef.current = structuredClone(events);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(events)]);

  /** True when a still-relevant event was checked and its venue is taken. */
  const hasUnavailableVenue = events.some((event, i) => {
    const availability = availabilityMap[i];
    return Boolean(
      availability?.checked &&
        availability.availabilty === false &&
        isAvailabilityRelevantStatus(event.actual_status) &&
        hasAvailabilityInputs(event),
    );
  });

  /** Hide the booking-details popup for an event, keeping its availability result. */
  const dismissBookingDetails = (eventIndex: number) => {
    setAvailabilityMap((m) =>
      m[eventIndex] ? { ...m, [eventIndex]: { ...m[eventIndex], details: null } } : m,
    );
  };

  return { availabilityMap, hasUnavailableVenue, dismissBookingDetails };
};
